if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              // EA 12.10.2026 (Fable-auditen A1 #2): en gammel handlekurv kan gi 422 «allerede utsolgt» selv om varen er
              // på lager. Vis en vennlig melding med «Tøm handlekurven og prøv igjen», og merk ikke knappen som utsolgt.
              if (response.status === 422) {
                const feiltekst = response.description || response.message || '';
                this.eaKurvFeil(feiltekst);
                this.dispatchCartErrorEvent(feiltekst, 'INVALID');
                linesUpdateDeferred?.reject(new Error(feiltekst));
                return;
              }
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          // EA 12.10.2026 (Fable-auditen A1 #3): vis feilen også når kunden brukte den klebrige kjøpsknappen langt nede.
          const r = this.errorMessageWrapper.getBoundingClientRect();
          if (r.top < 0 || r.bottom > window.innerHeight) {
            const header = document.querySelector('.section-header');
            const html = document.documentElement;
            const before = html.style.scrollBehavior;
            html.style.scrollBehavior = 'auto';
            window.scrollTo(0, Math.max(0, r.top + window.scrollY - (header ? header.offsetHeight : 0) - 16));
            html.style.scrollBehavior = before;
          }
        }
      }

      // EA 12.10.2026 (Fable-auditen A1 #2): vennlig melding ved 422 fra handlekurven.
      eaKurvFeil(feiltekst) {
        if (this.hideErrors) return;
        const utsolgt = /utsolgt|sold out/i.test(feiltekst);
        let melding = feiltekst || window.cartStrings.error;
        if (utsolgt && !this.eaKurvTomt) {
          melding = 'Varen kunne ikke legges i handlekurven. Det skyldes som regel en gammel handlekurv i nettleseren, ikke at varen er utsolgt.';
        } else if (utsolgt) {
          melding = 'Det gikk fortsatt ikke. Skriv til support@egelandacoustics.no, så ordner jeg bestillingen.';
        }
        this.handleErrorMessage(melding);
        if (!this.errorMessageWrapper) return;
        let knapp = this.errorMessageWrapper.querySelector('[data-ea-tom-kurv]');
        if (utsolgt && !this.eaKurvTomt) {
          if (!knapp) {
            knapp = document.createElement('button');
            knapp.type = 'button';
            knapp.className = 'link';
            knapp.setAttribute('data-ea-tom-kurv', '');
            knapp.style.cssText = 'flex-basis:100%;margin:.8rem 0 0;padding:0;text-align:left;font-size:1.4rem;';
            knapp.textContent = 'Tøm handlekurven og prøv igjen';
            knapp.addEventListener('click', () => this.eaTomKurvOgProvIgjen(knapp));
            this.errorMessageWrapper.style.flexWrap = 'wrap';
            this.errorMessageWrapper.appendChild(knapp);
          }
          knapp.hidden = false;
          knapp.style.display = '';
          knapp.disabled = false;
        } else if (knapp) {
          // Dawns .link har display: inline-block, så hidden alene skjuler ikke knappen.
          knapp.hidden = true;
          knapp.style.display = 'none';
        }
      }

      eaTomKurvOgProvIgjen(knapp) {
        knapp.disabled = true;
        this.eaKurvTomt = true;
        fetch(`${window.routes.cart_url}/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } })
          .then((r) => {
            if (!r.ok) throw new Error(String(r.status));
            knapp.hidden = true;
            knapp.style.display = 'none';
            this.handleErrorMessage();
            if (this.form.requestSubmit) this.form.requestSubmit();
            else this.submitButton.click();
          })
          .catch(() => {
            knapp.hidden = true;
            knapp.style.display = 'none';
            this.handleErrorMessage('Handlekurven kunne ikke tømmes. Last siden på nytt og prøv igjen, eller skriv til support@egelandacoustics.no.');
          });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}